import { safeJson } from "@/lib/http";
import { supabase } from "@/lib/supabase";

const BASE = process.env.REACT_APP_BACKEND_URL;

async function token() {
    const { data } = await supabase.auth.getSession();
    const t = data.session?.access_token;
    if (!t) throw new Error("Session expirée, reconnectez-vous.");
    return t;
}

async function call(path, method = "GET", body) {
    const r = await fetch(`${BASE}${path}`, { method, headers: { Authorization: `Bearer ${await token()}`, ...(body ? { "Content-Type": "application/json" } : {}) }, body: body ? JSON.stringify(body) : undefined });
    if (r.status === 204) return null;
    const j = await safeJson(r);
    if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`);
    return j;
}

export const listPrevisionnels = (dossierId) => call(`/api/dossiers/${dossierId}/previsionnels`);
export const createPrevisionnel = (dossierId, body) => call(`/api/dossiers/${dossierId}/previsionnels`, "POST", body || {});
export const getPrevisionnel = (id) => call(`/api/previsionnels/${id}`);
export const updatePrevisionnel = (id, body) => call(`/api/previsionnels/${id}`, "PATCH", body);
export const deletePrevisionnel = (id) => call(`/api/previsionnels/${id}`, "DELETE");
export const fetchPassifAuto = (dossierId) => call(`/api/dossiers/${dossierId}/passif-admis-auto`);

// SUPER-LOT 105 — Business Plan IA (async 202 + polling) & Magic Fill Emprunts
export const generateBusinessPlan = (prevId) => call(`/api/previsionnels/${prevId}/business-plan-async`, "POST", {});
export const pollBusinessPlan = (jobId, dossierId) => call(`/api/previsionnels/business-plan/${jobId}?dossier_id=${dossierId}`);
export const generateEmpruntsFill = (dossierId) => call(`/api/dossiers/${dossierId}/emprunts-fill-async`, "POST", {});
export const pollEmpruntsFill = (dossierId, jobId) => call(`/api/dossiers/${dossierId}/emprunts-fill/${jobId}`);
export const saveBusinessPlanMarkdown = (prevId, markdown) => call(`/api/previsionnels/${prevId}/business-plan`, "PATCH", { markdown });

// SUPER-LOT 121 — Constructeur de BP modulaire (sections JSONB) & Copilote M&A
export const getBpSections = (prevId) => call(`/api/previsionnels/${prevId}/bp/sections`);
export const saveBpSections = (prevId, sections) => call(`/api/previsionnels/${prevId}/bp/sections`, "PUT", { sections });
export const generateBpSections = (prevId) => call(`/api/previsionnels/${prevId}/bp/generate-async`, "POST", {});
export const pollBpSections = (jobId, dossierId) => call(`/api/previsionnels/bp/generate/${jobId}?dossier_id=${dossierId}`);
export const getCopilotHistory = (prevId) => call(`/api/previsionnels/${prevId}/bp/copilot/history`);
export const resetCopilot = (prevId) => call(`/api/previsionnels/${prevId}/bp/copilot`, "DELETE");

export async function streamCopilot(prevId, message, sections, { onStatus, onDelta, onDone, onError }) {
    const r = await fetch(`${BASE}/api/previsionnels/${prevId}/bp/copilot`, {
        method: "POST", headers: { Authorization: `Bearer ${await token()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ message, sections }),
    });
    if (!r.ok) { const j = await safeJson(r); onError(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`); return; }
    const reader = r.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
            if (!p.startsWith("data: ")) continue;
            let ev;
            try { ev = JSON.parse(p.slice(6)); } catch { continue; }
            if (ev.error) { await reader.cancel().catch(() => {}); onError(ev.error); return; }
            if (ev.delta) onDelta(ev.delta);
            else if (ev.status) onStatus?.(ev.status);
            else if (ev.done) onDone(ev);
        }
    }
}

export async function downloadPrevisionnelPdf(id) {
    const r = await fetch(`${BASE}/api/previsionnels/${id}/pdf`, { headers: { Authorization: `Bearer ${await token()}` } });
    if (!r.ok) { const j = await safeJson(r); throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`); }
    const blob = await r.blob();
    const name = (r.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || "Previsionnel.pdf";
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
}

export async function downloadPrevisionnelExcel(id) {
    const r = await fetch(`${BASE}/api/previsionnels/${id}/treso-detail.xlsx`, { headers: { Authorization: `Bearer ${await token()}` } });
    if (!r.ok) { const j = await safeJson(r); throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`); }
    const blob = await r.blob();
    const name = (r.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || "Detail_tresorerie.xlsx";
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
}
